import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminApi } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Search, Pencil, Unlock, Shield, Loader2, RefreshCw } from 'lucide-react';

export default function AdminEntries() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  // Edit state
  const [editEntry, setEditEntry] = useState(null);
  const [editIpa, setEditIpa] = useState('');
  const [editDefinitions, setEditDefinitions] = useState('');
  const [editSynonyms, setEditSynonyms] = useState('');
  const [editCollocates, setEditCollocates] = useState('');
  const [editDerivations, setEditDerivations] = useState('');
  const [editExamples, setEditExamples] = useState('');
  const [editRelated, setEditRelated] = useState('');
  const [saving, setSaving] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        adminApi.getEntries({ search: search || undefined }),
        adminApi.getCount(),
      ]);
      setEntries(listRes.data || []);
      setTotalCount(countRes.data?.count || 0);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load entries');
      }
    } finally {
      setLoading(false);
    }
  }, [search, navigate]);

  useEffect(() => {
    const t = setTimeout(loadEntries, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadEntries, search]);

  const openEdit = (entry) => {
    const d = entry.data || {};
    setEditEntry(entry);
    setEditIpa(d.ipa || '');
    setEditDefinitions(
      d.definitions?.length
        ? JSON.stringify(d.definitions, null, 2)
        : ''
    );
    setEditSynonyms((d.synonyms || []).join(', '));
    setEditCollocates((d.collocates || []).join(', '));
    setEditDerivations((d.derivations || []).join(', '));
    setEditExamples((d.examples || []).join('\n'));
    setEditRelated((d.related_expressions || []).join(', '));
  };

  const handleSave = async () => {
    if (!editEntry) return;
    setSaving(true);
    try {
      let defs = [];
      if (editDefinitions.trim()) {
        try {
          defs = JSON.parse(editDefinitions);
        } catch {
          toast.error('Definitions must be valid JSON');
          setSaving(false);
          return;
        }
      }
      const data = {
        ipa: editIpa.trim() || undefined,
        definitions: defs,
        synonyms: editSynonyms ? editSynonyms.split(',').map((s) => s.trim()).filter(Boolean) : [],
        collocates: editCollocates ? editCollocates.split(',').map((s) => s.trim()).filter(Boolean) : [],
        derivations: editDerivations ? editDerivations.split(',').map((s) => s.trim()).filter(Boolean) : [],
        examples: editExamples ? editExamples.split('\n').map((s) => s.trim()).filter(Boolean) : [],
        related_expressions: editRelated ? editRelated.split(',').map((s) => s.trim()).filter(Boolean) : [],
      };
      await adminApi.updateEntry(editEntry.word, editEntry.language_code, data);
      toast.success(`"${editEntry.word}" updated`);
      setEditEntry(null);
      loadEntries();
    } catch {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async (entry) => {
    try {
      await adminApi.unlockEntry(entry.word, entry.language_code);
      toast.success(`"${entry.word}" unlocked`);
      loadEntries();
    } catch {
      toast.error('Failed to unlock entry');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-entries-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-amber-400" />
          <div>
            <h1 className="text-2xl font-bold" data-testid="admin-page-title">Entry Edit</h1>
            <p className="text-sm text-muted-foreground">
              Manage locked word entries &mdash; {totalCount} total
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={loadEntries} data-testid="refresh-entries-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search words..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="search-entries-input"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="no-entries-msg">
          {search ? `No entries matching "${search}"` : 'No finalized entries yet. Close a word popover in the reader to create one.'}
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden" data-testid="entries-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Word</th>
                <th className="text-left px-4 py-3 font-medium">Language</th>
                <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">Definitions</th>
                <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Locked on</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, idx) => (
                <tr
                  key={`${entry.word}-${entry.language_code}`}
                  className={`border-t border-border ${idx % 2 === 0 ? '' : 'bg-muted/10'} hover:bg-muted/20 transition-colors`}
                  data-testid={`entry-row-${entry.word}`}
                >
                  <td className="px-4 py-3 font-mono font-semibold">{entry.word}</td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary" className="text-xs">{entry.language_code}</Badge>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-muted-foreground">
                    {entry.data?.definitions?.length ?? 0}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground text-xs">
                    {formatDate(entry.locked_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(entry)}
                        data-testid={`edit-entry-${entry.word}`}
                        className="gap-1"
                      >
                        <Pencil className="h-3 w-3" /> Edit
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1 text-amber-500 hover:text-amber-400"
                            data-testid={`unlock-entry-${entry.word}`}
                          >
                            <Unlock className="h-3 w-3" /> Unlock
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Unlock &ldquo;{entry.word}&rdquo;?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will remove the lock. The word&apos;s data can be refreshed again from the reader popover.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleUnlock(entry)}
                              className="bg-amber-600 hover:bg-amber-500"
                            >
                              Unlock
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editEntry} onOpenChange={(open) => !open && setEditEntry(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-entry-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4" />
              Edit: <span className="font-mono">{editEntry?.word}</span>
              <Badge variant="secondary">{editEntry?.language_code}</Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* IPA */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">IPA</label>
              <Input
                value={editIpa}
                onChange={(e) => setEditIpa(e.target.value)}
                placeholder="e.g. həˈloʊ"
                data-testid="edit-ipa-input"
              />
            </div>

            {/* Definitions — JSON */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Definitions (JSON array of {'{pos, definition, examples}'}')
              </label>
              <Textarea
                value={editDefinitions}
                onChange={(e) => setEditDefinitions(e.target.value)}
                rows={6}
                className="font-mono text-xs"
                placeholder='[{"pos": "n.", "definition": "...", "examples": []}]'
                data-testid="edit-definitions-input"
              />
            </div>

            {/* Synonyms */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Synonyms (comma-separated)
              </label>
              <Input
                value={editSynonyms}
                onChange={(e) => setEditSynonyms(e.target.value)}
                placeholder="word1, word2, word3"
                data-testid="edit-synonyms-input"
              />
            </div>

            {/* Collocates */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Collocates (comma-separated)
              </label>
              <Input
                value={editCollocates}
                onChange={(e) => setEditCollocates(e.target.value)}
                placeholder="strong coffee, black coffee"
                data-testid="edit-collocates-input"
              />
            </div>

            {/* Derivations */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Derivations (comma-separated)
              </label>
              <Input
                value={editDerivations}
                onChange={(e) => setEditDerivations(e.target.value)}
                placeholder="quickly, quickness"
                data-testid="edit-derivations-input"
              />
            </div>

            {/* Examples */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Example sentences (one per line)
              </label>
              <Textarea
                value={editExamples}
                onChange={(e) => setEditExamples(e.target.value)}
                rows={4}
                placeholder="He said hello as he walked in.&#10;Hello is a common greeting."
                data-testid="edit-examples-input"
              />
            </div>

            {/* Related expressions */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Related expressions (comma-separated)
              </label>
              <Input
                value={editRelated}
                onChange={(e) => setEditRelated(e.target.value)}
                placeholder="say hello, hello world"
                data-testid="edit-related-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEntry(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-entry-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
